/**
 * Auto Add Product
 * Automatically adds a product to cart when cart total reaches configured price threshold
 * OR when a specific product or product from a collection is in the cart
 */
class AutoAddProduct extends HTMLElement {
  constructor() {
    super();
    this.productId = this.dataset.productId;
    this.pricePln = Math.round(Number(this.dataset.pricePln) * 100) || 0;
    this.priceEur = Math.round(Number(this.dataset.priceEur) * 100) || 0;
    this.priceUsd = Math.round(Number(this.dataset.priceUsd) * 100) || 0;
    this.triggerProductId = this.dataset.triggerProductId;
    this.triggerCollectionId = this.dataset.triggerCollectionId;
    this.addProductId = this.dataset.addProductId;
    // State for price-based trigger
    this.productAdded = false;
    this.previousCartHadProduct = false;
    this.manuallyRemoved = false;
    // State for product/collection trigger
    this.triggerProductAdded = false;
    this.previousTriggerCartHadProduct = false;
    this.triggerManuallyRemoved = false;
    this.checking = false;
    this.triggerCollectionProducts = null; // Cache for collection products
  }

  connectedCallback() {
    const hasPriceTrigger = this.productId && this.productId !== '0' && this.productId.trim() !== '';
    const hasProductTrigger = this.triggerProductId && this.triggerProductId !== '0' && this.triggerProductId.trim() !== '';
    const hasCollectionTrigger = this.triggerCollectionId && this.triggerCollectionId !== '0' && this.triggerCollectionId.trim() !== '';
    const hasAddProduct = this.addProductId && this.addProductId !== '0' && this.addProductId.trim() !== '';

    // Need at least one valid configuration
    if (!hasPriceTrigger && !(hasAddProduct && (hasProductTrigger || hasCollectionTrigger))) {
      return;
    }

    this.initializeCartState();

    this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
      this.onCartUpdate.bind(this)
    );

    this.cartRemoveListener = this.onCartItemRemoved.bind(this);
    document.addEventListener('cart:updated', this.cartRemoveListener);
  }

  async initializeCartState() {
    try {
      const response = await fetch(`${FoxTheme.routes.cart_url}`, {
        ...FoxTheme.utils.fetchConfig(),
      });
      const cart = await response.json();

      // Check for price-based product
      if (this.productId && this.productId !== '0' && this.productId.trim() !== '') {
        const productInCart = this.isProductInCart(cart, this.productId);
        if (productInCart) {
          this.productAdded = true;
          this.previousCartHadProduct = true;
        }
      }

      // Check for product/collection trigger product
      if (this.addProductId && this.addProductId !== '0' && this.addProductId.trim() !== '') {
        const addProductInCart = this.isProductInCart(cart, this.addProductId);
        if (addProductInCart) {
          this.triggerProductAdded = true;
          this.previousTriggerCartHadProduct = true;
        }
      }

      // Preload collection products if needed
      if (this.triggerCollectionId && this.triggerCollectionId !== '0' && this.triggerCollectionId.trim() !== '') {
        await this.loadCollectionProducts();
      }
    } catch (error) {
      console.error('AutoAddProduct: Error initializing cart state', error);
    }
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    if (this.cartRemoveListener) {
      document.removeEventListener('cart:updated', this.cartRemoveListener);
    }
  }

  onCartItemRemoved(event) {
    if (event.detail && event.detail.cart) {
      setTimeout(() => {
        this.checkCart(event.detail.cart);
      }, 100);
    }
  }

  async onCartUpdate(event) {
    if (event.cart && event.cart.items) {
      await this.checkCart(event.cart);
    } else {
      await this.checkCart();
    }
  }

  async checkCart(cartData = null) {
    if (this.checking) return;
    this.checking = true;

    try {
      let cart = cartData;
      if (!cart) {
        const response = await fetch(`${FoxTheme.routes.cart_url}`, {
          ...FoxTheme.utils.fetchConfig(),
        });
        cart = await response.json();
      }

      // Handle price-based trigger
      if (this.productId && this.productId !== '0' && this.productId.trim() !== '') {
        await this.checkPriceBasedTrigger(cart);
      }

      // Handle product/collection trigger
      if (this.addProductId && this.addProductId !== '0' && this.addProductId.trim() !== '') {
        await this.checkProductCollectionTrigger(cart);
      }
    } catch (error) {
      console.error('AutoAddProduct: Error checking cart', error);
    } finally {
      this.checking = false;
    }
  }

  async checkPriceBasedTrigger(cart) {
    const productInCart = this.isProductInCart(cart, this.productId);

    const currentCurrency = (window.Shopify?.currency?.active || 'USD').toUpperCase();
    const cartTotal = cart.total_price || 0;

    let threshold = 0;
    switch (currentCurrency) {
      case 'PLN':
        threshold = this.pricePln;
        break;
      case 'EUR':
        threshold = this.priceEur;
        break;
      case 'USD':
        threshold = this.priceUsd;
        break;
      default:
        threshold = this.priceUsd;
    }

    const wasRemoved = this.previousCartHadProduct && !productInCart;

    if (wasRemoved && this.productAdded && threshold > 0 && cartTotal >= threshold) {
      this.manuallyRemoved = true;
      this.productAdded = false;
    }

    this.previousCartHadProduct = productInCart;

    if (productInCart) {
      if (this.manuallyRemoved) {
        this.manuallyRemoved = false;
        this.productAdded = false; 
      }

      // If product is in cart and was auto-added, check if we should remove it
      // Remove if cart total is below threshold and product was auto-added
      if (this.productAdded && threshold > 0 && cartTotal < threshold && !this.manuallyRemoved) {
        await this.removeProductFromCart(cart, this.productId);
        return;
      }

      return;
    }

    if (threshold === 0) {
      return;
    }

    const shouldResetFlags = cart.item_count === 0 || cartTotal < threshold;
    if (shouldResetFlags) {
      if (this.manuallyRemoved || this.productAdded) {
        this.manuallyRemoved = false;
        this.productAdded = false;
       
        this.previousCartHadProduct = productInCart;
      }
      return;
    }

    if (this.manuallyRemoved && cartTotal >= threshold) {
      return;
    }

    if (cartTotal >= threshold && !this.productAdded && !this.manuallyRemoved) {
      await this.addProductToCart(this.productId);
    }
  }

  async checkProductCollectionTrigger(cart) {
    const addProductInCart = this.isProductInCart(cart, this.addProductId);
    
    // Check if trigger condition is met
    let triggerMet = false;
    
    // Check for trigger product
    if (this.triggerProductId && this.triggerProductId !== '0' && this.triggerProductId.trim() !== '') {
      triggerMet = this.isProductInCart(cart, this.triggerProductId);
    }
    
    // Check for trigger collection
    if (!triggerMet && this.triggerCollectionId && this.triggerCollectionId !== '0' && this.triggerCollectionId.trim() !== '') {
      triggerMet = await this.isCollectionProductInCart(cart);
    }

    const wasRemoved = this.previousTriggerCartHadProduct && !addProductInCart;

    if (wasRemoved && this.triggerProductAdded && triggerMet) {
      this.triggerManuallyRemoved = true;
      this.triggerProductAdded = false;
    }

    this.previousTriggerCartHadProduct = addProductInCart;

    if (addProductInCart) {
      if (this.triggerManuallyRemoved) {
        this.triggerManuallyRemoved = false;
        this.triggerProductAdded = false; 
      }

      // If product is in cart and was auto-added, check if we should remove it
      // Remove if trigger condition is no longer met and product was auto-added
      if (this.triggerProductAdded && !triggerMet && !this.triggerManuallyRemoved) {
        await this.removeProductFromCart(cart, this.addProductId);
        this.triggerProductAdded = false;
        this.previousTriggerCartHadProduct = false;
        return;
      }

      return;
    }

    // If trigger condition is not met, reset flags
    if (!triggerMet) {
      if (this.triggerManuallyRemoved || this.triggerProductAdded) {
        this.triggerManuallyRemoved = false;
        this.triggerProductAdded = false;
        this.previousTriggerCartHadProduct = addProductInCart;
      }
      return;
    }

    // If manually removed and trigger is still met, don't re-add
    if (this.triggerManuallyRemoved && triggerMet) {
      return;
    }

    // Add product if trigger condition is met
    if (triggerMet && !this.triggerProductAdded && !this.triggerManuallyRemoved) {
      await this.addProductToCart(this.addProductId);
    }
  }

  isProductInCart(cart, productId) {
    return cart.items.some(
      (item) => {
        const itemProductId = String(item.product_id);
        const itemHandle = item.handle || item.product?.handle || '';
        return (
          itemProductId === String(productId) ||
          itemHandle === String(productId)
        );
      }
    );
  }

  async loadCollectionProducts() {
    if (this.triggerCollectionProducts !== null) {
      return; // Already loaded
    }

    try {
      // Try fetching collection products using products.json endpoint
      const collectionUrl = `${FoxTheme.routes.shop_url}/collections/${this.triggerCollectionId}/products.json`;
      const response = await fetch(collectionUrl);
      if (response.ok) {
        const data = await response.json();
        this.triggerCollectionProducts = data.products || [];
        return;
      }
      
      // If that fails, try the .js endpoint
      const collectionJsUrl = `${FoxTheme.routes.shop_url}/collections/${this.triggerCollectionId}.js`;
      const jsResponse = await fetch(collectionJsUrl);
      if (jsResponse.ok) {
        const collection = await jsResponse.json();
        this.triggerCollectionProducts = collection.products || [];
        return;
      }
      
      // If both fail, set empty array
      console.warn('AutoAddProduct: Could not load collection products from either endpoint');
      this.triggerCollectionProducts = [];
    } catch (error) {
      console.warn('AutoAddProduct: Could not load collection', error);
      this.triggerCollectionProducts = [];
    }
  }

  async isCollectionProductInCart(cart) {
    if (!this.triggerCollectionProducts) {
      await this.loadCollectionProducts();
    }

    if (!this.triggerCollectionProducts || this.triggerCollectionProducts.length === 0) {
      return false;
    }

    const collectionProductIds = this.triggerCollectionProducts.map(p => String(p.id));
    const collectionHandles = this.triggerCollectionProducts.map(p => p.handle || '').filter(h => h);

    return cart.items.some((item) => {
      const itemProductId = String(item.product_id);
      const itemHandle = item.handle || item.product?.handle || '';
      
      return (
        collectionProductIds.includes(itemProductId) ||
        (itemHandle && collectionHandles.includes(itemHandle))
      );
    });
  }

  async removeProductFromCart(cart, productId = null) {
    try {
      const targetProductId = productId || this.productId;
      if (!targetProductId) return;

      // Find all cart items matching this product
      const itemsToRemove = cart.items.filter((item) => {
        const itemProductId = String(item.product_id);
        const itemHandle = item.handle || item.product?.handle || '';
        return (
          itemProductId === String(targetProductId) ||
          itemHandle === String(targetProductId)
        );
      });

      if (itemsToRemove.length === 0) {
        return;
      }

      // Get keys of items to remove
      const keysToRemove = itemsToRemove.map((item) => item.key);

      console.log('AutoAddProduct: Removing product from cart', { keys: keysToRemove, productId: targetProductId });

      // Get sections to update
      let sectionsToUpdate = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToUpdate } })
      );

      // Remove items from cart by setting quantity to 0
      const updates = Object.fromEntries(keysToRemove.map((key) => [key, 0]));
      const body = JSON.stringify({
        updates: updates,
        sections: sectionsToUpdate,
        section_url: window.location.pathname,
      });

      const response = await fetch(`${FoxTheme.routes.cart_update_url}`, {
        ...FoxTheme.utils.fetchConfig('javascript'),
        body: body,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('AutoAddProduct: HTTP error removing product', response.status, errorText);
        return;
      }

      let result;
      try {
        result = await response.json();
      } catch (jsonError) {
        console.error('AutoAddProduct: Failed to parse JSON response', jsonError);
        return;
      }

      if (result.status) {
        console.error('AutoAddProduct: Error removing product', result.description || result.message);
        return;
      }

      // Get updated cart with sections
      const cartResponse = await fetch(`${FoxTheme.routes.cart_url}`, {
        ...FoxTheme.utils.fetchConfig(),
      });
      const cartJson = await cartResponse.json();

      // Merge sections from cart update response
      if (result.sections) {
        cartJson['sections'] = result.sections;
      }

      // Publish cart update event
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
        cart: cartJson,
      });

      // Reset state
      this.productAdded = false;
      this.previousCartHadProduct = false;
    } catch (error) {
      console.error('AutoAddProduct: Error removing product from cart', error);
    }
  }

  async addProductToCart(productId = null) {
    const targetProductId = productId || this.productId;
    if (!targetProductId) return;
    
    // Check appropriate flag based on which product we're adding
    const isPriceTrigger = targetProductId === this.productId;
    const isTriggerProduct = targetProductId === this.addProductId;
    
    if (isPriceTrigger && this.productAdded) return;
    if (isTriggerProduct && this.triggerProductAdded) return;

    try {
      let product = null;
      let variantId = null;

      const productUrl = `${FoxTheme.routes.shop_url}/products/${targetProductId}.js`;

      try {
        const productResponse = await fetch(productUrl);
        if (productResponse.ok) {
          product = await productResponse.json();
        }
      } catch (fetchError) {
        console.warn('AutoAddProduct: Could not fetch product JSON, trying section method');
      }

      // If we got product data, find the first available variant
      if (product && product.variants && product.variants.length > 0) {
        const variant = product.variants.find((v) => v.available) || product.variants[0];
        variantId = variant.id;
        console.log('AutoAddProduct: Found variant ID from product JSON', variantId);
      }

      // If we still don't have a variant ID, try getting it from product page section
      if (!variantId) {
        try {
          const sectionResponse = await fetch(
            `${FoxTheme.routes.root_url}products/${targetProductId}?section_id=main-product`
          );
          if (sectionResponse.ok) {
            const sectionHtml = await sectionResponse.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(sectionHtml, 'text/html');
            const variantInput = doc.querySelector('input[name="id"], input[data-variant-id]');
            if (variantInput) {
              variantId = variantInput.value || variantInput.getAttribute('data-variant-id');
              console.log('AutoAddProduct: Found variant ID from section', variantId);
            }
          }
        } catch (sectionError) {
          console.warn('AutoAddProduct: Could not get variant from section');
        }
      }

      if (!variantId) {
        console.error('AutoAddProduct: Could not determine variant ID for product', targetProductId);
        return;
      }

      console.log('AutoAddProduct: Adding variant to cart', { variantId, productId: targetProductId });

      let sectionsToUpdate = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToUpdate } })
      );

      const body = JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: 1,
          },
        ],
        sections: sectionsToUpdate,
        section_url: window.location.pathname,
      });

      const response = await fetch(`${FoxTheme.routes.cart_add_url}`, {
        ...FoxTheme.utils.fetchConfig('javascript'),
        body: body,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('AutoAddProduct: HTTP error adding product', response.status, errorText);
        return;
      }

      let result;
      try {
        result = await response.json();
      } catch (jsonError) {
        console.error('AutoAddProduct: Failed to parse JSON response', jsonError);
        return;
      }

      if (result.status) {
        console.error('AutoAddProduct: Error adding product', result.description || result.message);
        return;
      }

      const cartResponse = await fetch(`${FoxTheme.routes.cart_url}`, {
        ...FoxTheme.utils.fetchConfig(),
      });
      const cartJson = await cartResponse.json();

      if (result.sections) {
        cartJson['sections'] = result.sections;
      }

      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
        cart: cartJson,
      });

      document.dispatchEvent(
        new CustomEvent('product-ajax:added', {
          detail: {
            product: result,
          },
        })
      );

      if (FoxTheme.settings.cartType === 'drawer') {
        setTimeout(() => {
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && !cartDrawer.open) {
            cartDrawer.show();
          }
        }, 300);
      }

      // Set appropriate flag based on which product was added
      if (isPriceTrigger) {
        this.productAdded = true;
      } else if (isTriggerProduct) {
        this.triggerProductAdded = true;
      }
    } catch (error) {
      console.error('AutoAddProduct: Error adding product to cart', error);
    }
  }
}

customElements.define('auto-add-product', AutoAddProduct);